import type {
  AlertRuleJsonld,
  AssetJsonld,
  CapabilityJsonld,
  EventJsonld,
  EventStatisticJsonld,
  ExtensionJsonld,
  GroupJsonld,
  ManifestJsonld,
  MetricSeriesJsonld,
  UptimeSummaryJsonld,
  OperationJsonld,
} from './generated'

/**
 * The seam between what the core's specification promises and what the interface renders.
 *
 * ## Why this file exists
 *
 * Two properties of the generated types make them awkward to render directly, and both are
 * properties of the *specification*, not of this dashboard:
 *
 * 1. **Nothing is required.** API Platform emits a `required` list for input schemas, where
 *    validation constraints tell it what is mandatory, and none at all for output schemas. So
 *    every field of every resource arrives as `T | undefined`, including `id`. That is not wrong —
 *    it is what the document says — but a table cell cannot be written against it without a guard
 *    per column, and a guard per column is where somebody eventually writes `!`.
 *
 * 2. **Several object-valued fields are typed as arrays.** `Operation.params`, `Event.payload`,
 *    `AlertRule.condition`, `Capability.options`, `MetricSeries.points` and the rest come through
 *    as `Array<string | null>`, because API Platform inferred them from a PHP `array` type hint and
 *    cannot tell a list from a map. The wire values are objects.
 *
 * Both are reported to the core. Neither is patched in `src/api/generated/`, because that
 * directory is overwritten wholesale by `npm run api:sync` and a hand edit there would be lost
 * silently — which is the exact failure this whole reconciliation exists to undo.
 *
 * So the narrowing happens **here**, once, in a file that says out loud what it is doing. Every
 * `??` below is a decision about what an absent field means, and each one follows the same rule
 * (`CONVENTIONS.md` §5): **`null` is unknown, and unknown is not zero.** A missing
 * `extension_update_count` becomes `null` — "we have not been told" — never `0`, which would say
 * "we checked and there are none".
 *
 * The view types mirror the server's field names exactly. Renaming a field here would build a
 * second vocabulary, and the next person would have to hold both.
 */

// --- narrowing helpers -------------------------------------------------------------------------

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null

const num = (value: unknown): number | null => (typeof value === 'number' ? value : null)

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []

/** An object-valued field the specification types as an array. `null` when it is genuinely absent. */
const object = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const objectOrEmpty = (value: unknown): Record<string, unknown> => object(value) ?? {}

/**
 * The id of a resource, or `''`.
 *
 * The empty string is not a real id and nothing will match it; it exists so that a row missing an
 * id renders as an inert row rather than crashing the table. A resource without an id has already
 * gone wrong somewhere upstream, and the place to notice that is a support conversation, not a
 * blank screen.
 */
const id = (value: unknown): string => (typeof value === 'string' ? value : '')

// --- open vocabularies -------------------------------------------------------------------------

/**
 * Vocabularies the core documents in prose and types on the wire as open `string`s.
 *
 * They live here, as unions, because the interface has to choose an icon and a colour per value
 * and a `Record<AssetKind, …>` is what makes "you added a kind and forgot the icon" a compile
 * error. They are **not** a claim that the server's set is closed: every value that arrives is
 * rendered, and the components fall back rather than throw. The spellings are copied from the
 * query-parameter descriptions in the generated specification.
 */
export type AssetKind = 'server' | 'site' | 'domain' | 'endpoint'
export type HealthState = 'unknown' | 'ok' | 'warning' | 'critical'
export type AvailabilityState = 'up' | 'suspect' | 'down' | 'recovering'

/** Narrow an open server value to a known one, or to the honest default. */
export const asAssetKind = (value: string | null | undefined): AssetKind =>
  value === 'server' || value === 'site' || value === 'domain' || value === 'endpoint'
    ? value
    : 'endpoint'

export const asHealthState = (value: string | null | undefined): HealthState =>
  value === 'ok' || value === 'warning' || value === 'critical' || value === 'unknown'
    ? value
    : 'unknown'

export const asAvailabilityState = (
  value: string | null | undefined,
): AvailabilityState | undefined =>
  value === 'up' || value === 'suspect' || value === 'down' || value === 'recovering'
    ? value
    : undefined

// --- asset -----------------------------------------------------------------------------------

export interface AssetView {
  id: string
  kind: string
  label: string
  health: string
  group_id: string | null
  parent_id: string | null
  tags: string[]
  url: string | null
  hostname: string | null
  domain_name: string | null
  product_family: string | null
  product_version: string | null
  php_version: string | null
  agent_name: string | null
  agent_version: string | null
  availability: string | null
  availability_since: string | null
  last_seen_at: string | null
  manifest_at: string | null
  domain_expires_at: string | null
  /** Declared capability ids, as the list endpoint reports them. Enough to gate a bulk action. */
  capabilities: string[]
  extension_count: number | null
  extension_update_count: number | null
  extension_vulnerable_count: number | null
  in_flight_operation_count: number | null
  supports_agent: boolean
  archived: boolean
  archived_at: string | null
  created_at: string | null
}

export const toAssetView = (asset: AssetJsonld): AssetView => ({
  id: id(asset.id),
  kind: str(asset.kind) ?? 'unknown',
  // The label falls back to the id rather than to an empty cell: a row you cannot name is a row
  // you cannot report, and the id is at least something to paste into a search box.
  label: str(asset.label) ?? id(asset.id),
  health: str(asset.health) ?? 'unknown',
  group_id: str(asset.group_id),
  parent_id: str(asset.parent_id),
  tags: strings(asset.tags),
  url: str(asset.url),
  hostname: str(asset.hostname),
  domain_name: str(asset.domain_name),
  product_family: str(asset.product_family),
  product_version: str(asset.product_version),
  php_version: str(asset.php_version),
  agent_name: str(asset.agent_name),
  agent_version: str(asset.agent_version),
  availability: str(asset.availability),
  availability_since: str(asset.availability_since),
  last_seen_at: str(asset.last_seen_at),
  manifest_at: str(asset.manifest_at),
  domain_expires_at: str(asset.domain_expires_at),
  capabilities: strings(asset.capabilities),
  extension_count: num(asset.extension_count),
  extension_update_count: num(asset.extension_update_count),
  extension_vulnerable_count: num(asset.extension_vulnerable_count),
  in_flight_operation_count: num(asset.in_flight_operation_count),
  supports_agent: bool(asset.supports_agent, false),
  archived: bool(asset.archived, false),
  archived_at: str(asset.archived_at),
  created_at: str(asset.created_at),
})

// --- group -----------------------------------------------------------------------------------

export interface GroupView {
  id: string
  label: string
  description: string | null
  asset_count: number | null
  archived: boolean
}

export const toGroupView = (group: GroupJsonld): GroupView => ({
  id: id(group.id),
  label: str(group.label) ?? id(group.id),
  description: str(group.description),
  asset_count: num(group.asset_count),
  archived: bool(group.archived, false),
})

// --- capability ------------------------------------------------------------------------------

export interface CapabilityView {
  id: string
  namespace: string
  declared: boolean
  registered: boolean
  stepped: boolean
  mutates: boolean
  requires_reauthentication: boolean
  options: Record<string, unknown>
  agent_binding: string | null
}

export const toCapabilityView = (capability: CapabilityJsonld): CapabilityView => ({
  id: id(capability.id),
  namespace: str(capability.namespace) ?? id(capability.id).split('.')[0],
  declared: bool(capability.declared, true),
  registered: bool(capability.registered, false),
  stepped: bool(capability.stepped, false),
  // Pessimistic where the core did not say: assuming a capability is harmless is how something
  // destructive gets dispatched without a confirmation.
  mutates: bool(capability.mutates, true),
  requires_reauthentication: bool(capability.requires_reauthentication, true),
  options: objectOrEmpty(capability.options),
  agent_binding: str(capability.agent_binding),
})

// --- manifest --------------------------------------------------------------------------------

export interface ManifestView {
  asset_id: string
  fetched_at: string | null
  protocol: string | null
  agent_name: string | null
  agent_version: string | null
  agent_instance_id: string | null
  product_family: string | null
  product_version: string | null
  agent_base_url: string | null
  last_seen_at: string | null
  /** Declared capability ids, in the order the agent sent them. */
  capabilities: string[]
  /** The agent's declared limits, verbatim. Rendered as data; never interpreted. */
  limits: Record<string, unknown> | null
  /** The whole manifest document as fetched, for the "show me exactly what it said" panel. */
  manifest: Record<string, unknown> | null
}

export const toManifestView = (manifest: ManifestJsonld): ManifestView => ({
  asset_id: id(manifest.asset_id),
  fetched_at: str(manifest.fetched_at),
  protocol: str(manifest.protocol),
  agent_name: str(manifest.agent_name),
  agent_version: str(manifest.agent_version),
  agent_instance_id: str(manifest.agent_instance_id),
  product_family: str(manifest.product_family),
  product_version: str(manifest.product_version),
  agent_base_url: str(manifest.agent_base_url),
  last_seen_at: str(manifest.last_seen_at),
  capabilities: strings(manifest.capabilities),
  limits: object(manifest.limits),
  manifest: object(manifest.manifest),
})

// --- extension -------------------------------------------------------------------------------

export interface ExtensionView {
  id: string
  asset_id: string
  slug: string
  type: string
  name: string
  version: string | null
  available_version: string | null
  /** `null` is "we do not know", which is not "up to date". */
  update_available: boolean | null
  security_update: boolean
  update_severity: string | null
  enabled: boolean
  protected: boolean
  vulnerable: boolean | null
  vulnerable_hint: boolean | null
  vulnerability_checked_at: string | null
  author: string | null
  url: string | null
  installed_at: string | null
  updated_at: string | null
}

export const toExtensionView = (extension: ExtensionJsonld): ExtensionView => ({
  id: id(extension.id),
  asset_id: id(extension.asset_id),
  slug: str(extension.slug) ?? '',
  type: str(extension.type) ?? 'unknown',
  name: str(extension.name) ?? str(extension.slug) ?? id(extension.id),
  version: str(extension.version),
  available_version: str(extension.available_version),
  update_available: typeof extension.update_available === 'boolean' ? extension.update_available : null,
  security_update: bool(extension.security_update, false),
  update_severity: str(extension.update_severity),
  enabled: bool(extension.enabled, false),
  protected: bool(extension.protected, false),
  vulnerable: typeof extension.vulnerable === 'boolean' ? extension.vulnerable : null,
  vulnerable_hint: typeof extension.vulnerable_hint === 'boolean' ? extension.vulnerable_hint : null,
  vulnerability_checked_at: str(extension.vulnerability_checked_at),
  author: str(extension.author),
  url: str(extension.url),
  installed_at: str(extension.installed_at),
  updated_at: str(extension.updated_at),
})

// --- operation -------------------------------------------------------------------------------

export interface OperationView {
  id: string
  asset_id: string
  capability: string
  state: string
  progress: number | null
  terminal: boolean
  params: Record<string, unknown>
  result: Record<string, unknown> | null
  error: Record<string, unknown> | null
  blocked_reason: string | null
  message: string | null
  attempt: number | null
  retryable: boolean
  requested_by: string | null
  started_at: string | null
  last_step_at: string | null
  finished_at: string | null
  created_at: string | null
  reauthenticated: boolean
}

const TERMINAL_STATES: ReadonlySet<string> = new Set(['done', 'error', 'blocked'])

export const toOperationView = (operation: OperationJsonld): OperationView => {
  const state = str(operation.state) ?? 'queued'

  return {
    id: id(operation.id),
    asset_id: id(operation.asset_id),
    capability: str(operation.capability) ?? '',
    state,
    progress: num(operation.progress),
    // The core answers this itself. The state-name check is a fallback for a fixture or an older
    // core, not a second opinion.
    terminal: bool(operation.terminal, TERMINAL_STATES.has(state)),
    params: objectOrEmpty(operation.params),
    result: object(operation.result),
    error: object(operation.error),
    blocked_reason: str(operation.blocked_reason),
    message: str(operation.message),
    attempt: num(operation.attempt),
    retryable: bool(operation.retryable, false),
    requested_by: str(operation.requested_by),
    started_at: str(operation.started_at),
    last_step_at: str(operation.last_step_at),
    finished_at: str(operation.finished_at),
    created_at: str(operation.created_at),
    reauthenticated: bool(operation.reauthenticated, false),
  }
}

// --- event -----------------------------------------------------------------------------------

export interface EventView {
  id: string
  asset_id: string
  asset_label: string | null
  event_uuid: string | null
  occurred_at: string | null
  category: string
  action: string
  severity: number | null
  severity_label: string | null
  severe: boolean
  /** Who did it, as the binding reported it. Free-form; rendered, never matched on. */
  actor: Record<string, unknown> | null
  /** What it was done to. Same rule. */
  target: Record<string, unknown> | null
  source_ip: string | null
  user_agent: string | null
  payload: Record<string, unknown> | null
  op_id: string | null
  retention_exempt: boolean
}

export const toEventView = (event: EventJsonld): EventView => ({
  id: id(event.id),
  asset_id: id(event.asset_id),
  asset_label: str(event.asset_label),
  event_uuid: str(event.event_uuid),
  occurred_at: str(event.occurred_at),
  category: str(event.category) ?? 'system',
  action: str(event.action) ?? '',
  severity: num(event.severity),
  severity_label: str(event.severity_label),
  severe: bool(event.severe, false),
  actor: object(event.actor),
  target: object(event.target),
  source_ip: str(event.source_ip),
  user_agent: str(event.user_agent),
  payload: object(event.payload),
  op_id: str(event.op_id),
  retention_exempt: bool(event.retention_exempt, false),
})

// --- event statistics ------------------------------------------------------------------------

export interface EventStatisticView {
  bucket: string
  count: number
  severe_count: number
  asset_id: string | null
  category: string | null
  action: string | null
}

export const toEventStatisticView = (row: EventStatisticJsonld): EventStatisticView => ({
  bucket: str(row.bucket) ?? '',
  // Zero here is honest: this resource *is* a count, and a bucket the core returned without one
  // is a bucket with nothing in it.
  count: num(row.count) ?? 0,
  severe_count: num(row.severe_count) ?? 0,
  asset_id: str(row.asset_id),
  category: str(row.category),
  action: str(row.action),
})

// --- metrics ---------------------------------------------------------------------------------

/**
 * One point of a series, exactly as `MetricSeriesResource::fromRow` builds it.
 *
 * `value: null` is a gap in the data, not a zero reading — a chart must break its line there
 * rather than draw through it. `samples` is how many raw readings the bucket rolled up, which is
 * what tells a reader whether a spike is a spike or a single sample.
 */
export interface MetricPoint {
  at: string
  value: number | null
  samples: number | null
}

export interface MetricSeriesView {
  asset_id: string
  asset_label: string | null
  metric: string
  interval: string | null
  aggregate: string | null
  tags: Record<string, unknown>
  points: MetricPoint[]
}

const toPoints = (value: unknown): MetricPoint[] => {
  if (!Array.isArray(value)) return []

  return value.flatMap((entry): MetricPoint[] => {
    if (entry === null || typeof entry !== 'object') return []
    const point = entry as Record<string, unknown>
    const at = str(point.at)
    if (at === null) return []
    return [{ at, value: num(point.value), samples: num(point.samples) }]
  })
}

export const toMetricSeriesView = (series: MetricSeriesJsonld): MetricSeriesView => ({
  asset_id: id(series.asset_id),
  asset_label: str(series.asset_label),
  metric: str(series.metric) ?? '',
  interval: str(series.interval),
  aggregate: str(series.aggregate),
  tags: objectOrEmpty(series.tags),
  points: toPoints(series.points),
})

export interface UptimeView {
  asset_id: string
  from: string | null
  to: string | null
  /** `null` means no availability history for the window — not 100%, and not 0%. */
  uptime_ratio: number | null
  current_state: string | null
  current_state_since: string | null
  outage_count: number | null
  downtime_seconds: number | null
  average_response_ms: number | null
  last_checked_at: string | null
}

export const toUptimeView = (
  uptime: UptimeSummaryJsonld,
): UptimeView => ({
  asset_id: id(uptime.asset_id),
  from: str(uptime.from),
  to: str(uptime.to),
  uptime_ratio: num(uptime.uptime_ratio),
  current_state: str(uptime.current_state),
  current_state_since: str(uptime.current_state_since),
  outage_count: num(uptime.outage_count),
  downtime_seconds: num(uptime.downtime_seconds),
  average_response_ms: num(uptime.average_response_ms),
  last_checked_at: str(uptime.last_checked_at),
})

// --- alert rules -----------------------------------------------------------------------------

/**
 * A node of the declarative condition tree.
 *
 * `{all:[…]} | {any:[…]} | {not:{…}} | {always:bool} | {fact, op, value}`, nested up to 16 levels
 * — the core's own words, from the `condition` description on `AlertRule.AlertRuleInput`.
 */
export interface AlertCondition {
  all?: AlertCondition[]
  any?: AlertCondition[]
  not?: AlertCondition
  always?: boolean
  fact?: string
  op?: string
  value?: unknown
}

export interface AlertRuleView {
  id: string
  label: string
  scope_type: string
  scope_id: string | null
  condition: AlertCondition
  severity: string
  channels: string[]
  throttle_seconds: number
  enabled: boolean
  /** Facts this rule needs. A rule requiring one nothing reports will never fire. */
  required_facts: string[]
  created_at: string | null
}

export const toAlertRuleView = (rule: AlertRuleJsonld): AlertRuleView => ({
  id: id(rule.id),
  label: str(rule.label) ?? id(rule.id),
  scope_type: str(rule.scope_type) ?? 'all',
  scope_id: str(rule.scope_id),
  condition: (object(rule.condition) ?? {}) as AlertCondition,
  severity: str(rule.severity) ?? 'error',
  channels: strings(rule.channels),
  // The core documents 3600 as its own default, so an unstated value is that, not "never throttle".
  throttle_seconds: num(rule.throttle_seconds) ?? 3600,
  enabled: bool(rule.enabled, false),
  required_facts: strings(rule.required_facts),
  created_at: str(rule.created_at),
})
